"use client";

import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { AnnouncementBar } from "./nav/announcement-bar";

/** Combined sticky offset: announcement (~36px) + nav row (72px / 88px) + bottom curve depth (12px) */
export const HEADER_NAV_TOP_CLASS = "top-[calc(2.25rem+72px+12px)] lg:top-[calc(2.25rem+88px+12px)]";

type HeaderScrollHideContextValue = {
	setSuppressScrollHide: (suppress: boolean) => void;
};

const HeaderScrollHideContext = createContext<HeaderScrollHideContextValue | null>(null);

export function useHeaderScrollHideSuppression(): (suppress: boolean) => void {
	const ctx = useContext(HeaderScrollHideContext);
	return ctx?.setSuppressScrollHide ?? (() => {});
}

export function HeaderScrollShadow({
	children,
	campaignAnnouncement,
}: {
	children: ReactNode;
	campaignAnnouncement?: ReactNode;
}) {
	const [hidden, setHidden] = useState(false);
	const suppressRef = useRef(false);

	const setSuppressScrollHide = (suppress: boolean) => {
		suppressRef.current = suppress;
		if (suppress) setHidden(false);
	};

	useEffect(() => {
		let lastY = window.scrollY;
		let ticking = false;
		const THRESHOLD = 6;
		const TOP_BUFFER = 120;

		const onScroll = () => {
			if (suppressRef.current) return;
			if (ticking) return;
			ticking = true;
			requestAnimationFrame(() => {
				const y = window.scrollY;
				if (y < TOP_BUFFER) setHidden(false);
				else if (y > lastY + THRESHOLD) setHidden(true);
				else if (y < lastY - THRESHOLD) setHidden(false);
				lastY = y;
				ticking = false;
			});
		};

		window.addEventListener("scroll", onScroll, { passive: true });
		return () => window.removeEventListener("scroll", onScroll);
	}, []);

	return (
		<HeaderScrollHideContext.Provider value={{ setSuppressScrollHide }}>
			<header
				className={`sticky top-0 z-50 transition-transform duration-300 ease-out ${
					hidden ? "-translate-y-full" : "translate-y-0"
				}`}
			>
				<AnnouncementBar campaign={campaignAnnouncement} />
				{/* Thin primary line ABOVE the nav body — datasheet detail */}
				<div aria-hidden className="bg-primary/25 h-px" />
				{/* Nav body + curved bottom edge (SVG path), drop-shadow under curve */}
				<div className="relative">
					{/*
						SVG curve mathematics:
						- Nav row: 72px (mobile) / 88px (lg). Curve depth: 12px (shallow).
						- Total: 84 / 100. viewBox uses desktop ratio (88+12=100) for 1:1 mapping on lg.
						- Curve START at y=88 (nav bottom). Subtle dip to y=100 at center.
					*/}
					<svg
						aria-hidden
						viewBox="0 0 1440 100"
						preserveAspectRatio="none"
						className="pointer-events-none absolute inset-x-0 top-0 h-[calc(100%+0.75rem)] w-full"
					>
						<path d="M0 0 L1440 0 L1440 88 Q720 100 0 88 Z" fill="var(--background)" fillOpacity="0.97" />
						<path
							d="M0 88 Q720 100 1440 88"
							fill="none"
							stroke="var(--primary)"
							strokeWidth="1"
							strokeLinecap="round"
						/>
					</svg>
					<div className="relative mx-auto flex h-[72px] max-w-7xl items-center justify-between gap-3 px-4 sm:px-5 lg:h-[88px] lg:gap-6 lg:px-8">
						{children}
					</div>
					{/* Reserve 0.75rem below for the curve's depth — keeps subsequent content from clipping */}
					<div aria-hidden className="h-3 w-full" />
				</div>
			</header>
		</HeaderScrollHideContext.Provider>
	);
}
